/**
 * Build a crawler-friendly preview URL for social sharing.
 *
 * The `og-preview` Supabase edge function serves a tiny HTML document with
 * proper Open Graph / Twitter / product tags for the given blog post or
 * product, then immediately redirects humans to the real page. Social
 * scrapers (Facebook, X, WhatsApp, LinkedIn, iMessage, Slack, Pinterest)
 * read the OG tags and render a rich preview; humans never see the stub.
 */

export type SharePreviewType = "blog" | "product";

interface BuildShareUrlOptions {
  type: SharePreviewType;
  slug?: string | null;
  /** Real human URL — used as a fallback when slug is missing or env is unset. */
  fallbackUrl: string;
}

function supabaseBase(): string | null {
  const base = (import.meta.env.VITE_SUPABASE_URL as string | undefined)?.replace(/\/+$/, "");
  return base || null;
}

export function buildShareUrl({ type, slug, fallbackUrl }: BuildShareUrlOptions): string {
  if (!slug) return fallbackUrl;
  const base = supabaseBase();
  if (!base) return fallbackUrl;

  const params = new URLSearchParams({ type, slug });
  return `${base}/functions/v1/og-preview?${params.toString()}`;
}

/**
 * Returns the tall (1000×1500) Pinterest-optimised pin image URL for a blog
 * post, or null when the edge function base is unavailable.
 */
export function buildPinImageUrl(slug?: string | null): string | null {
  if (!slug) return null;
  const base = supabaseBase();
  if (!base) return null;
  return `${base}/functions/v1/pin-image?slug=${encodeURIComponent(slug)}`;
}
